/**
 * Cross-app link registry — the blessed Flow deep-link intents, one
 * canonical shape each, per `docs/flow/unified-shell-and-deeplinking.md`
 * §4 ("Cross-app link registry").
 *
 * Same-app links use `useAppBasePath()` (`./base-path.tsx`) since the base
 * is mount-dependent (`/tools/<slug>` vs `/flow/<slug>`) and only the host
 * knows it. Cross-app links are different: the *caller* doesn't have (and
 * shouldn't need) the target app's base-path context, so these functions
 * hardcode the target's canonical base internally — but only in this one
 * module.
 *
 * Canonical bases (flipped 2026-07-24, Spencer): cross-app links into Flow
 * point at the unified `/flow/command-center` shell. Action View and
 * Playhouse were retired (plan 2026-10-13-001): their old `/flow|/tools`
 * URLs redirect permanently to the Signals / Plays tabs
 * (`web/app-home/src/components/flow/legacy-action-view-redirect.tsx`), so
 * no builder here targets them. Launcher is NOT a Flow app and stays at
 * `/tools/launcher`.
 *
 * Registry (one builder per intent): `flowHome`, `accountInCommandCenter`,
 * `accountTabInCommandCenter` (an account on a named `?accountTab=`),
 * `contactInCommandCenter` (the account's People tab, contact expanded), `viewInCommandCenter`, `contactsInCommandCenter`,
 * `signalInCommandCenter` (the Signals-tab `?signal=` deep link — the
 * pinned-row landing for Slack "Open in Terra"), `playInCommandCenter`,
 * `searchInCommandCenter`, `playbooksIndex`, `campaignManagerInLauncher`.
 *
 * All functions return ORIGIN-RELATIVE URLs (no scheme/host) — the prior
 * hardcoded `https://terra.clay.com/...` in both Action View "T" icons
 * pointed at prod even on staging/dev; origin-relative fixes that as a
 * side effect of centralizing.
 */
// Relative, not `@shared/flow/filter-fields` — Signals tab PR 1, review
// round 1, R6: `tsconfig.tests.json` has no `@shared/*` path (only
// `tsconfig.miniapps.json` and `web/app-home/tsconfig.json` declare it), so
// the alias import failed `typecheck:tests`. Type-only, so this creates no
// runtime import edge and the mini-apps `no-restricted-imports` "cannot
// import from core src/" rule doesn't fire on it (that rule targets real
// module specifiers reaching into `src/`, and a type-only import is erased
// before it would matter here regardless).
import type { FlowViewEntity } from '../../src/shared/flow/filter-fields';
// Runtime import (a plain const string), unlike the type-only imports above: `PLAYS_VIEW_PARAM` lives in core `src/` beside the plays table.
import { PLAYS_VIEW_PARAM } from '../../src/shared/flow/plays-table';
import type { AccountDrilldownTab } from '../../src/shared/flow/account-drilldown-tabs';

export const COMMAND_CENTER_BASE = '/flow/command-center';
/** The query param that names the tab an account drilldown opens on (`AccountDrilldownTabSchema`). */
export const ACCOUNT_TAB_PARAM = 'accountTab';
const LAUNCHER_BASE = '/tools/launcher';
const PLAYBOOKS_BASE = '/tools/playbooks';

/** Command Center home on a given `?tab=` subtab (string: _shared must not import command-center types). */
export function flowHome(tab: string): string {
  return `${COMMAND_CENTER_BASE}?tab=${tab}`;
}

/** Open an account's drill-down in Command Center. */
export function accountInCommandCenter(accountId: string): string {
  return `${COMMAND_CENTER_BASE}/accounts/${encodeURIComponent(accountId)}`;
}

/** Open an account's drill-down in Command Center on a named tab (`?accountTab=`). */
export function accountTabInCommandCenter(accountId: string, tab: AccountDrilldownTab): string {
  return `${accountInCommandCenter(accountId)}?${ACCOUNT_TAB_PARAM}=${tab}`;
}

/**
 * Open a contact inside its account's drill-down in Command Center: `?accountTab=people` opens the
 * People tab and `?contact=` auto-expands the contact in the roster. `?contact=` never picks a tab.
 */
export function contactInCommandCenter(accountId: string, contactId: string): string {
  return `${accountTabInCommandCenter(accountId, 'people')}&contact=${encodeURIComponent(contactId)}`;
}

/**
 * Signals tab PR 1, review round 1, R4/D-D — `entity` does NOT map 1:1 onto
 * Command Center's `?tab=` subtab value: `mini-apps/command-center/index.tsx`'s
 * `subtabValues` literal set is `['account', 'contact', 'plays', 'signals']`
 * (plural, matching the tab bar's "Accounts"/"Plays" labeling convention),
 * but `FlowViewEntity` is `'contact' | 'account' | 'signal' | 'play'` (singular,
 * matching every OTHER per-entity name in the shared flow modules —
 * `SIGNAL_FIELDS`, `SIGNAL_PRESETS`, etc.). A bare pass-through therefore
 * built `?tab=signal`, which isn't one of Command Center's literal values,
 * so Browse-all's Open/Copy link for a Signals view silently landed on
 * Accounts (the subtab hook's own unmatched-value fallback) instead of
 * throwing — the exact kind of link that "works" in that it 200s and
 * navigates somewhere, just not where the rep clicked. `contact`/`account`
 * are already spelled identically on both sides, so the map is a no-op
 * for them and only `signal` and `play` differ.
 */
const COMMAND_CENTER_TAB_FOR_ENTITY: Record<FlowViewEntity, string> = {
  contact: 'contact',
  account: 'account',
  signal: 'signals',
  play: 'plays',
};

/** Per-entity saved-view URL param: Plays has its own (`view_play`) because Command Center keeps every tab on one URL. */
const VIEW_PARAM_FOR_ENTITY: Record<FlowViewEntity, string> = {
  contact: 'view',
  account: 'view',
  signal: 'view',
  play: PLAYS_VIEW_PARAM,
};

/**
 * Open a saved view or preset in Command Center on the view's OWN entity tab
 * (U3 — Browse-all's cross-entity Open/Copy link). `viewId` is a bare
 * `flow_views.id` or preset slug; the receiving `useViewStateMachine`
 * resolves it exactly as it resolves any other `?view=` link
 * (first-resolution precedence, `useViewStateMachine.ts`).
 *
 * Callers building a copy-link href (not just a `navigate()` target) must
 * prefix this with `window.location.origin` themselves — this module only
 * ever returns origin-relative URLs (see file header).
 */
export function viewInCommandCenter(entity: FlowViewEntity, viewId: string): string {
  return `${COMMAND_CENTER_BASE}?tab=${COMMAND_CENTER_TAB_FOR_ENTITY[entity]}&${VIEW_PARAM_FOR_ENTITY[entity]}=${encodeURIComponent(viewId)}`;
}

/**
 * Open the GLOBAL Contacts subtab in Command Center, focused on one contact
 * (Q4, QA round 3 — "View on contact" must work from anywhere in the
 * product). Used when the caller has no account context to drill into (e.g.
 * EnrollQueueContext's "View on contact" navigate-away fallback, which only
 * ever carries a contactId). `?contact=` is now the single reader/writer
 * param on EVERY `ContactFlowTable` mount (global and account-scoped alike
 * — see `ContactFlowTable.tsx`'s `contactQueryParam` doc comment): the
 * global mount scrolls+flashes the row via its DOM-query jump AND expands
 * it (expansion is URL-driven on every mount now), and the param persists
 * in the URL rather than stripping itself after the jump.
 */
export function contactsInCommandCenter(contactId: string): string {
  return `${COMMAND_CENTER_BASE}?tab=contact&contact=${encodeURIComponent(contactId)}`;
}

/**
 * Open the Signals tab with one signal expanded (`?signal=`). If the row is
 * not in the rep's current view, `useSignalTableModel` fetches it by id and
 * pins it with an "Opened from link — not in this view" chip. `source` (e.g.
 * `slack_notification`) is the click-attribution tag. Tab literal is the
 * plural `signals` (Command Center's `subtabValues`). No `contact=` — it is
 * inert on the Signals tab.
 */
export function signalInCommandCenter(signalId: string, source?: string): string {
  const base = `${COMMAND_CENTER_BASE}?tab=signals&signal=${encodeURIComponent(signalId)}`;
  return source ? `${base}&source=${encodeURIComponent(source)}` : base;
}

/**
 * Open a play selected in Command Center's Plays subtab (plan
 * `2026-09-24-005` U15a). Used by the Plays tab journey (U15b) for links
 * that need to reproduce a play selection outside the tab itself — the
 * in-tab "Filter for this play" menu action (plan 2026-09-25-002 U5) selects
 * via `?play=` directly and doesn't need this.
 */
export function playInCommandCenter(playId: string): string {
  return `${COMMAND_CENTER_BASE}?tab=plays&play=${encodeURIComponent(playId)}`;
}

/**
 * Open Command Center's omnibox pre-seeded with a query (`?q=`). Consumed by
 * the short entity entry routes' fallback path (`EntityEntryRoute` /
 * `ContactRedirect` in `web/app-home/src/components/flow/entity-routes.tsx`)
 * when the incoming id/text doesn't classify as a recognized SFDC id, or a
 * `/contact/:id` lookup comes back `kind: 'none'`/errors.
 *
 * Deliberately no `accountShortLink()`/`contactShortLink()` builders exist in
 * this file (plan D4): the short routes are inbound-only entry points, not a
 * new outbound link shape — all in-app links keep using the existing
 * canonical drilldown builders above.
 */
export function searchInCommandCenter(q: string): string {
  return `${COMMAND_CENTER_BASE}?q=${encodeURIComponent(q)}`;
}

// accountInTerra() (core /accounts/{id}/intelligence) was removed 2026-07-24:
// the drift between it and accountInCommandCenter() was resolved in favor of
// the Command Center drill-down as THE "open account in Terra" intent for reps
// (Spencer; standard doc §6 → Resolved). Re-add only if a surface genuinely
// needs the core intelligence view as a distinct intent.

/** Open Playbooks at its index (e.g. launcher's legacy /v2 redirect). */
export function playbooksIndex(): string {
  return PLAYBOOKS_BASE;
}

/** Open a campaign's manager page in Win Tool (launcher). */
export function campaignManagerInLauncher(campaignId: string): string {
  return `${LAUNCHER_BASE}/${encodeURIComponent(campaignId)}/manager`;
}
